import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, Phone, MessageCircle } from "lucide-react";
import { BRAND, NAV } from "@/data/brand";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDrop, setOpenDrop] = useState(null);
  const dropTimer = useRef(null);
  const [mobileDropOpen, setMobileDropOpen] = useState(null);
  const { pathname } = useLocation();
  const navRef = useRef(null);

  // Sync nav height as CSS var
  useEffect(() => {
    const update = () => {
      const h = navRef.current?.offsetHeight || 76;
      document.documentElement.style.setProperty("--nav-h", `${h}px`);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setMobileDropOpen(null); setOpenDrop(null); }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleDropEnter = (key) => {
    if (dropTimer.current) { clearTimeout(dropTimer.current); dropTimer.current = null; }
    setOpenDrop(key);
  };
  const handleDropLeave = () => {
    dropTimer.current = setTimeout(() => setOpenDrop(null), 220);
  };

  return (
    <header
      ref={navRef}
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full ${
        scrolled ? "bg-white nav-scrolled" : "bg-white"
      }`}
    >
      <div className="container-x flex items-center justify-between gap-6 py-3 md:py-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
          <img
            src="/img/logo.webp"
            alt={BRAND.name}
            width={168}
            height={56}
            fetchPriority="high"
            decoding="async"
            className="h-11 md:h-14 w-auto object-contain select-none"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.href}
                className="relative"
                onMouseEnter={() => handleDropEnter(item.href)}
                onMouseLeave={handleDropLeave}
              >
                <button
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`nav-underline flex items-center gap-1 px-4 py-2 text-[14px] font-semibold tracking-wide text-[color:var(--ink)]`}
                >
                  {item.label}
                  <ChevronDown className={`w-4 h-4 transition-transform ${openDrop === item.href ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {openDrop === item.href && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute left-0 top-full pt-3 min-w-[220px]"
                    >
                      <div
                        onMouseEnter={() => handleDropEnter(item.href)}
                        onMouseLeave={handleDropLeave}
                        className="rounded-2xl bg-white border border-neutral-100 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.18)] p-2"
                      >
                        {item.children.map((c) => (
                          <NavLink
                            key={c.href}
                            to={c.href}
                            data-testid={`nav-sub-${c.label.toLowerCase()}`}
                            className={({ isActive }) =>
                              `flex items-center gap-3 px-4 py-3 rounded-xl text-[14px] font-semibold transition-colors ${
                                isActive
                                  ? "bg-[color:var(--fucsia)] text-white"
                                  : "text-[color:var(--ink)] hover:bg-[color:var(--paper)]"
                              }`
                            }
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--fucsia)]" />
                            {c.label}
                          </NavLink>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                key={item.href}
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase()}`}
                end={item.href === "/"}
                className={({ isActive }) =>
                  `nav-underline px-4 py-2 text-[14px] font-semibold tracking-wide text-[color:var(--ink)] ${
                    isActive ? "active text-[color:var(--fucsia)]" : ""
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid="nav-cta-call"
            className="btn-pill btn-primary text-[13px] py-2.5 px-5"
          >
            <Phone className="w-4 h-4" /> Chiama
          </a>
        </div>

        {/* Mobile burger */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-neutral-200 shrink-0"
          aria-label={mobileOpen ? "Chiudi menu" : "Apri menu"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu overlay — portalized to <body> so it works regardless of scroll position */}
      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              key="mobile-panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="lg:hidden fixed inset-0 z-[100] bg-white"
              data-testid="nav-mobile-panel"
              style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
            >
              <div className="flex flex-col h-full">
                <div className="container-x flex items-center justify-between py-3 border-b border-neutral-100">
                  <Link to="/" onClick={() => setMobileOpen(false)}>
                    <img src="/img/logo.webp" alt={BRAND.name} className="h-11 w-auto object-contain" />
                  </Link>
                  <div className="w-11 h-11" aria-hidden="true" />
                </div>

                {/* Floating close X — sempre in alto a destra */}
                <button
                  onClick={() => setMobileOpen(false)}
                  aria-label="Chiudi menu"
                  data-testid="nav-mobile-close"
                  className="fixed top-3 right-4 z-[110] inline-flex items-center justify-center w-11 h-11 rounded-full bg-white border border-neutral-200 shadow-lg"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="flex-1 overflow-y-auto container-x pt-6 pb-10">
                  <ul className="divide-y divide-neutral-100">
                    {NAV.map((item, i) => (
                      <motion.li
                        key={item.href}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.025 * i, duration: 0.25 }}
                      >
                        {item.children ? (
                          <div>
                            <button
                              onClick={() => setMobileDropOpen((v) => v === item.href ? null : item.href)}
                              className="w-full flex items-center justify-between py-4 font-display text-2xl text-left"
                              aria-expanded={mobileDropOpen === item.href}
                            >
                              <span>{item.label}</span>
                              <ChevronDown className={`w-5 h-5 transition-transform ${mobileDropOpen === item.href ? "rotate-180 text-[color:var(--fucsia)]" : ""}`} />
                            </button>
                            <AnimatePresence>
                              {mobileDropOpen === item.href && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                  className="overflow-hidden"
                                >
                                  <div className="pb-4 flex flex-col gap-2">
                                    {item.children.map((c) => (
                                      <NavLink
                                        key={c.href}
                                        to={c.href}
                                        onClick={() => setMobileOpen(false)}
                                        className={({ isActive }) =>
                                          `flex items-center gap-3 py-3 px-4 rounded-2xl text-[15px] font-semibold ${
                                            isActive ? "bg-[color:var(--fucsia)] text-white" : "bg-[color:var(--paper)] text-[color:var(--ink)]"
                                          }`
                                        }
                                      >
                                        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--fucsia)]" />
                                        {c.label}
                                      </NavLink>
                                    ))}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ) : (
                          <NavLink
                            to={item.href}
                            end={item.href === "/"}
                            onClick={() => setMobileOpen(false)}
                            className={({ isActive }) =>
                              `flex items-center justify-between py-4 font-display text-2xl ${
                                isActive ? "text-[color:var(--fucsia)]" : "text-[color:var(--ink)]"
                              }`
                            }
                          >
                            {item.label}
                          </NavLink>
                        )}
                      </motion.li>
                    ))}
                  </ul>

                  <div className="mt-8 grid grid-cols-2 gap-3">
                    <a href={`tel:${BRAND.phoneRaw}`} onClick={() => setMobileOpen(false)} className="btn-pill btn-primary justify-center">
                      <Phone className="w-4 h-4" /> Chiama
                    </a>
                    <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn-pill btn-secondary justify-center">
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                  </div>

                  <div className="mt-8 text-[13px] text-neutral-500 leading-relaxed">
                    <div className="font-semibold text-[color:var(--ink)]">Vieni a trovarci</div>
                    <div className="mt-1">Via Inghilterra 39, 57128 Livorno · La Scopaia</div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
